"use client"

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react"

import { useLocale } from "@/lib/locale-context"
import type { Locale } from "@/lib/content"
import { Nico, type NicoAnim, type NicoMood } from "@/components/nico"

/*
 * Nico rides the page. A green and a purple line, side by side as Nexo draws
 * lines that share a street, snake through the page: down one margin, across
 * the top of each section (just above its rule, where he announces the stop
 * like a metro), then down the other margin, to the end of the line. Nico runs
 * along it as the page scrolls, always somewhere on screen, looks where you
 * point, fidgets when left alone, and cheers at the terminus.
 * Purely decorative: hidden from assistive tech, never in the tab order.
 */

const copy: Record<Locale, { start: string; end: string; next: string; lines: string[] }> = {
  en: {
    start: "Hi! Going somewhere?",
    end: "End of the line!",
    next: "Next stop:",
    lines: ["Still time for a coffee.", "I wave when yours arrives.", "I run when you're late.", "Tap me. I'm always watching."],
  },
  pt: {
    start: "Olá! Vamos a algum lado?",
    end: "Fim da linha!",
    next: "Próxima paragem:",
    lines: ["Ainda dá para um café.", "Aceno quando o teu chega.", "Corro quando tens pressa.", "Toca-me. Estou sempre de olho em ti."],
  },
  de: {
    start: "Hallo! Wohin geht's?",
    end: "Endstation!",
    next: "Nächster Halt:",
    lines: ["Noch Zeit für einen Kaffee.", "Ich winke, wenn deiner kommt.", "Ich renne, wenn du spät dran bist.", "Tipp mich an. Ich hab dich im Blick."],
  },
}

const INK = "#1C1A18"
const GREEN = "#1FA05A"
const PURPLE = "#8E44E8"
/** Distance between samples of the track (px). */
const STEP = 6
/** Where on screen Nico wants to be, as a share of the viewport height. */
const ANCHOR = 0.55
/** Corner radius of the track (px). */
const RADIUS = 36

type Pt = [number, number]
type Stop = { at: Pt; title: string; end?: boolean }
type Track = { width: number; height: number; points: Pt[]; stops: Stop[]; small: boolean }

/** Shift an axis-aligned polyline sideways by d (left of the direction of travel). */
function offset(points: Pt[], d: number): Pt[] {
  const normal = (a: Pt, b: Pt): Pt => {
    const dx = Math.sign(b[0] - a[0])
    const dy = Math.sign(b[1] - a[1])
    return [dy * d, -dx * d]
  }
  return points.map((p, i) => {
    const n1 = i > 0 ? normal(points[i - 1], p) : null
    const n2 = i < points.length - 1 ? normal(p, points[i + 1]) : null
    if (n1 && n2) return [p[0] + n1[0] + n2[0], p[1] + n1[1] + n2[1]]
    const n = (n1 ?? n2)!
    return [p[0] + n[0], p[1] + n[1]]
  })
}

/** An SVG path through the points with rounded corners, like a metro map. */
function rounded(points: Pt[], r: number): string {
  if (points.length < 2) return ""
  let d = `M${points[0][0]} ${points[0][1]}`
  for (let i = 1; i < points.length - 1; i++) {
    const [px, py] = points[i - 1]
    const [x, y] = points[i]
    const [nx, ny] = points[i + 1]
    const inLen = Math.hypot(x - px, y - py) || 1
    const outLen = Math.hypot(nx - x, ny - y) || 1
    const k = Math.min(r, inLen / 2, outLen / 2)
    const ax = x - ((x - px) / inLen) * k
    const ay = y - ((y - py) / inLen) * k
    const bx = x + ((nx - x) / outLen) * k
    const by = y + ((ny - y) / outLen) * k
    d += ` L${ax} ${ay} Q${x} ${y} ${bx} ${by}`
  }
  const last = points[points.length - 1]
  return `${d} L${last[0]} ${last[1]}`
}

export function NicoRide() {
  const { locale } = useLocale()
  const text = copy[locale]

  const layerRef = useRef<HTMLDivElement>(null)
  const centreRef = useRef<SVGPathElement>(null)
  const fillRefs = useRef<(SVGPathElement | null)[]>([])
  const riderRef = useRef<HTMLDivElement>(null)
  const nicoRef = useRef<HTMLDivElement>(null)

  const [track, setTrack] = useState<Track | null>(null)
  const [passed, setPassed] = useState(0)
  const [running, setRunning] = useState(false)
  const [facingLeft, setFacingLeft] = useState(false)
  const [gaze, setGaze] = useState<[number, number]>([0, 0])
  const [runGaze, setRunGaze] = useState<[number, number]>([0, 3])
  const [anim, setAnim] = useState<NicoAnim | null>(null)
  const [playN, setPlayN] = useState(0)
  const [mood, setMood] = useState<NicoMood>("idle")
  const [bubble, setBubble] = useState<string | null>(null)
  const [bubbleLeft, setBubbleLeft] = useState(false)

  // The track, sampled: arc length -> point. y never decreases along it.
  const samples = useRef<{ xs: Float32Array; ys: Float32Array; total: number; stopS: number[] } | null>(null)
  const cur = useRef(-1)
  const passedRef = useRef(0)
  const runningRef = useRef(false)
  const endRef = useRef(false)
  const lastMove = useRef(0)
  const bubbleTimer = useRef<ReturnType<typeof setTimeout>>(undefined)
  const animTimer = useRef<ReturnType<typeof setTimeout>>(undefined)

  const say = useCallback((line: string, ms = 2600) => {
    clearTimeout(bubbleTimer.current)
    setBubble(line)
    bubbleTimer.current = setTimeout(() => setBubble(null), ms)
  }, [])

  const play = useCallback((a: NicoAnim, ms: number, m: NicoMood = "idle") => {
    clearTimeout(animTimer.current)
    setAnim(a)
    setMood(m)
    setPlayN((n) => n + 1)
    animTimer.current = setTimeout(() => {
      setAnim(null)
      setMood("idle")
    }, ms)
  }, [])

  // Lay the track through the page: margins and the top of every section.
  useEffect(() => {
    const layer = layerRef.current
    const root = layer?.parentElement
    if (!layer || !root) return

    const measure = () => {
      const box = root.getBoundingClientRect()
      const shell = root.querySelector<HTMLElement>(".shell")
      if (!shell) return
      const sr = shell.getBoundingClientRect()
      const cs = getComputedStyle(shell)
      const padL = parseFloat(cs.paddingLeft)
      const padR = parseFloat(cs.paddingRight)
      const small = padL < 32
      const xL = sr.left - box.left + padL / 2
      const xR = sr.right - box.left - padR / 2
      const height = root.scrollHeight

      const rules = [...root.querySelectorAll<HTMLElement>("[data-nico-rule]")]
      const points: Pt[] = [[xR, 80]]
      const stops: Stop[] = []
      let right = true
      for (const rule of rules) {
        const y = rule.getBoundingClientRect().top - box.top - 14
        const here = right ? xR : xL
        const there = right ? xL : xR
        points.push([here, y], [there, y])
        stops.push({ at: [here, y], title: rule.closest("section")?.querySelector("h2")?.textContent ?? "" })
        right = !right
      }
      const endY = height - (small ? 28 : 40)
      const endX = right ? xR : xL
      points.push([endX, endY])
      stops.push({ at: [endX, endY], title: "", end: true })

      setTrack({ width: box.width, height, points, stops, small })
    }

    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(root)
    window.addEventListener("resize", measure)
    return () => {
      ro.disconnect()
      window.removeEventListener("resize", measure)
    }
  }, [])

  // Sample the drawn path, so Nico walks exactly on what is on screen.
  useLayoutEffect(() => {
    const path = centreRef.current
    if (!path || !track) return
    const total = path.getTotalLength()
    const n = Math.ceil(total / STEP) + 1
    const xs = new Float32Array(n)
    const ys = new Float32Array(n)
    for (let i = 0; i < n; i++) {
      const p = path.getPointAtLength(Math.min(total, i * STEP))
      xs[i] = p.x
      ys[i] = Math.max(p.y, i > 0 ? ys[i - 1] : p.y)
    }
    const stopS = track.stops.map(({ at }) => {
      let best = 0
      let bestD = Infinity
      for (let i = 0; i < n; i++) {
        const d = Math.hypot(xs[i] - at[0], ys[i] - at[1])
        if (d < bestD) {
          bestD = d
          best = i
        }
      }
      return best * STEP
    })
    samples.current = { xs, ys, total, stopS }
  }, [track])

  // The ride.
  useEffect(() => {
    let raf = 0
    let still = 0
    let prevX = 0
    let last = performance.now()
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches

    const pointAt = (s: number): Pt => {
      const sm = samples.current!
      const f = Math.max(0, Math.min(sm.xs.length - 1, s / STEP))
      const i = Math.floor(f)
      const j = Math.min(sm.xs.length - 1, i + 1)
      const t = f - i
      return [sm.xs[i] + (sm.xs[j] - sm.xs[i]) * t, sm.ys[i] + (sm.ys[j] - sm.ys[i]) * t]
    }

    const tick = (now: number) => {
      // Frame-rate independent: 1 at 60 fps.
      const k = Math.min(4, (now - last) / 16.7)
      last = now
      const sm = samples.current
      const layer = layerRef.current
      if (sm && layer && track) {
        const top = layer.getBoundingClientRect().top + window.scrollY
        const anchor = window.scrollY - top + window.innerHeight * ANCHOR
        // First sample at or below the anchor: y only grows, so bisect.
        let lo = 0
        let hi = sm.ys.length - 1
        if (anchor >= sm.ys[hi]) lo = hi
        else
          while (lo < hi) {
            const mid = (lo + hi) >> 1
            if (sm.ys[mid] < anchor) lo = mid + 1
            else hi = mid
          }
        const target = Math.min(sm.total, lo * STEP)

        if (cur.current < 0) cur.current = target
        const delta = target - cur.current
        // Eased, with a running speed; far behind (a jump, a fast fling) he sprints.
        const cap = Math.max(track.small ? 18 : 30, Math.abs(delta) * 0.06) * k
        const step = reduced
          ? delta
          : Math.sign(delta) * Math.min(Math.abs(delta), Math.max(1.5, Math.abs(delta) * 0.12) * k, cap)
        const before = pointAt(cur.current)
        cur.current += step
        const [x, y] = pointAt(cur.current)

        const size = track.small ? 46 : 60
        const h = (size * 88) / 84
        // On the line, but never cut off by the edge of the page.
        const left = Math.max(2, Math.min(track.width - size - 2, x - size / 2))
        if (riderRef.current) {
          riderRef.current.style.transform = `translate3d(${left}px,${y - h * 0.92}px,0)`
        }
        for (const el of fillRefs.current) el?.setAttribute("stroke-dasharray", `${cur.current} 999999`)

        const moving = Math.abs(step) > 0.4
        still = moving ? 0 : still + 1
        const run = still < 6
        if (run !== runningRef.current) {
          runningRef.current = run
          setRunning(run)
        }
        if (moving) {
          lastMove.current = performance.now()
          const dx = x - before[0]
          const dy = y - before[1]
          if (Math.abs(dx) > 0.3) setFacingLeft(dx < 0)
          const d = Math.hypot(dx, dy) || 1
          setRunGaze([Math.round((Math.abs(dx) / d) * 3), Math.round((dy / d) * 3)])
        }
        if (Math.abs(x - prevX) > 1) setBubbleLeft(x > track.width / 2)
        prevX = x

        // Stops: announce each one as he sets off along it.
        const n = sm.stopS.filter((s) => s <= cur.current + 1).length
        if (n !== passedRef.current) {
          const forward = n > passedRef.current
          passedRef.current = n
          setPassed(n)
          const stop = track.stops[n - 1]
          if (forward && stop && !stop.end && stop.title) say(`${text.next} ${stop.title}`, 2200)
        }

        const atEnd = cur.current >= sm.total - 2
        if (atEnd && !endRef.current) {
          endRef.current = true
          play("cheer", 1200, "happy")
          say(text.end, 3000)
        } else if (cur.current < sm.total - 200) {
          endRef.current = false
        }
      }
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [track, play, say, text.end, text.next])

  // Hello, and a little fidget whenever he is left standing.
  useEffect(() => {
    const hello = setTimeout(() => {
      play("wave", 1800)
      say(text.start, 3200)
    }, 900)
    const fidget = setInterval(() => {
      if (runningRef.current || performance.now() - lastMove.current < 5000) return
      const pick = Math.random()
      if (pick < 0.45) play("wave", 1800)
      else if (pick < 0.8) play("jump", 700, "happy")
      else play("jump", 700, "wow")
    }, 6500)
    return () => {
      clearTimeout(hello)
      clearInterval(fidget)
    }
  }, [play, say, text.start])

  // Eyes follow the pointer while he stands still.
  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      const el = nicoRef.current
      if (!el) return
      const r = el.getBoundingClientRect()
      const dx = e.clientX - (r.left + r.width / 2)
      const dy = e.clientY - (r.top + r.height / 2)
      const d = Math.hypot(dx, dy) || 1
      setGaze([Math.round((dx / d) * 30) / 10, Math.round((dy / d) * 30) / 10])
    }
    window.addEventListener("pointermove", onMove, { passive: true })
    return () => window.removeEventListener("pointermove", onMove)
  }, [])

  useEffect(
    () => () => {
      clearTimeout(bubbleTimer.current)
      clearTimeout(animTimer.current)
    },
    []
  )

  const onTap = () => {
    play("jump", 700, "happy")
    say(text.lines[Math.floor(Math.random() * text.lines.length)])
  }

  const size = track?.small ? 46 : 60
  const green = track ? rounded(offset(track.points, -2.5), RADIUS) : ""
  const purple = track ? rounded(offset(track.points, 2.5), RADIUS) : ""
  const flip = facingLeft && running

  return (
    <div
      ref={layerRef}
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 z-30 overflow-hidden print:hidden"
    >
      {track && (
        <svg width={track.width} height={track.height} className="absolute left-0 top-0" fill="none">
          <path ref={centreRef} d={rounded(track.points, RADIUS)} stroke="none" />
          <path d={green} stroke={GREEN} strokeWidth="3.5" strokeLinecap="round" opacity="0.22" />
          <path d={purple} stroke={PURPLE} strokeWidth="3.5" strokeLinecap="round" opacity="0.22" />
          <path
            ref={(el) => {
              fillRefs.current[0] = el
            }}
            d={green}
            stroke={GREEN}
            strokeWidth="3.5"
            strokeLinecap="round"
            strokeDasharray="0 999999"
          />
          <path
            ref={(el) => {
              fillRefs.current[1] = el
            }}
            d={purple}
            stroke={PURPLE}
            strokeWidth="3.5"
            strokeLinecap="round"
            strokeDasharray="0 999999"
          />
          {track.stops.map((stop, i) => (
            <circle
              key={i}
              cx={stop.at[0]}
              cy={stop.at[1]}
              r={stop.end ? 9 : 7}
              fill="#FFFFFF"
              strokeWidth="3"
              style={{ stroke: i < passed ? INK : "hsl(var(--border))", transition: "stroke .3s" }}
            />
          ))}
        </svg>
      )}

      {track && (
        <div ref={riderRef} className="absolute left-0 top-0 will-change-transform">
          <div ref={nicoRef} className="pointer-events-auto relative cursor-pointer" onClick={onTap}>
            <div style={{ transform: flip ? "scaleX(-1)" : undefined }}>
              <Nico
                size={size}
                mood={running ? "hurry" : mood}
                anim={running ? "run" : anim}
                playN={playN}
                gaze={running ? runGaze : gaze}
              />
            </div>
            {bubble && (
              <div className={bubbleLeft ? "absolute bottom-full right-1/2 mb-1" : "absolute bottom-full left-1/2 mb-1"}>
                <p
                  key={bubble}
                  className="nico-bubble nico-round w-max max-w-[15rem] rounded-[10px] border-2 bg-white px-3 py-1.5 text-[13px] font-semibold leading-snug"
                  style={{
                    color: INK,
                    borderColor: INK,
                    boxShadow: `0 3px 0 ${INK}`,
                    animation: "nico-pop .35s cubic-bezier(.3,1.6,.5,1)",
                    transformOrigin: bubbleLeft ? "100% 100%" : "0% 100%",
                  }}
                >
                  {bubble}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
